/**
 * Archive database (`db.snapshot`) inspector: a database connection, the
 * schema to archive, optionally a subset of its tables, and the retired-system
 * archive to write into.
 *
 * Reading one table or one SELECT is the Database Source node's job; this node
 * is the fan-out that a retired application needs — every table in a schema,
 * each preserved with its own manifest, checksum, column schema, primary key
 * and foreign keys, in one run.
 */

import { useEffect, useState } from "react";
import { MultiSelect, Select, Stack, Switch, TextInput } from "@mantine/core";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { databaseSchemas, databaseTables } from "@/shared/api/database";
import { listConnections } from "@/shared/api/integration";
import { listArchiveContainers } from "@/shared/api/archive";
import { toastApiError } from "@/shared/ui/toast";
import { ConnectionRefSelect } from "./ConnectionRefSelect";
import { useConnectionRef } from "./useConnectionRef";

type Option = { value: string; label: string };

/** Connections, schemas and tables for a database connection. */
function useDatabaseBrowse(connectionId: string, schema: string) {
  const getToken = useAccessToken();
  const [connections, setConnections] = useState<Option[]>([]);
  const [schemas, setSchemas] = useState<Option[]>([]);
  const [tables, setTables] = useState<Option[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const items = (await listConnections(token, "database")).items;
        if (!cancelled)
          setConnections(items.map((c) => ({ value: c.connection_id, label: c.display_name })));
      } catch (err) {
        toastApiError("Could not load connections", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    if (!connectionId) {
      setSchemas([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const items = (await databaseSchemas(token, connectionId)).items;
        if (!cancelled)
          setSchemas(
            items.map((s) => ({
              value: s.name,
              label: s.table_count ? `${s.name} (${s.table_count})` : s.name,
            })),
          );
      } catch (err) {
        if (!cancelled) toastApiError("Could not list schemas", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId]);

  useEffect(() => {
    if (!connectionId || !schema) {
      setTables([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const items = (await databaseTables(token, connectionId, schema)).items;
        if (!cancelled)
          setTables(
            items.map((t) => ({
              value: t.name,
              // The row estimate comes from the planner, not a COUNT(*) — it is
              // here to help someone pick a table, so "~" is the honest prefix.
              label:
                t.row_estimate != null ? `${t.name}  ~${t.row_estimate.toLocaleString()} rows` : t.name,
            })),
          );
      } catch (err) {
        if (!cancelled) toastApiError("Could not list tables", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId, schema]);

  return { connections, schemas, tables };
}

export function DbSnapshotConfig({
  config,
  editable,
  onChange,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
}) {
  const getToken = useAccessToken();
  // A node names a connection id or a `@role`; everything here takes an id.
  const {
    connectionId,
    role,
    settled: roleSettled,
  } = useConnectionRef(String(config.connectionId ?? ""));
  const schema = String(config.schema ?? "");
  const containerId = String(config.containerId ?? "");
  const selected = Array.isArray(config.tables) ? (config.tables as string[]) : [];
  const { connections, schemas, tables } = useDatabaseBrowse(connectionId, schema);
  const [containers, setContainers] = useState<Option[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const items = (await listArchiveContainers(token)).items;
        if (!cancelled)
          setContainers(
            items
              .filter((c) => c.kind === "datasets")
              .map((c) => ({ value: c.container_id, label: `${c.name} (${c.code})` })),
          );
      } catch (err) {
        toastApiError("Could not load archives", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  return (
    <Stack gap="xs">
      <ConnectionRefSelect
        label="Database connection"
        placeholder={connections.length ? "Select a connection" : "No database connections"}
        data={connections}
        nothingFoundMessage="No database connections"
        connectionId={connectionId}
        role={role}
        settled={roleSettled}
        editable={editable}
        onChange={(v) => {
          onChange("connectionId", v);
          onChange("connectionName", connections.find((c) => c.value === v)?.label ?? "");
          onChange("schema", "");
          onChange("tables", []);
        }}
      />
      <Select
        label="Schema to archive"
        placeholder={schemas.length ? "Select a schema" : "Pick a connection first"}
        data={schemas}
        value={schema || null}
        disabled={!editable || !schemas.length}
        searchable
        onChange={(v) => {
          onChange("schema", v ?? "");
          onChange("tables", []);
        }}
      />
      <MultiSelect
        label="Tables"
        description="Leave empty to archive every table in the schema — the usual case when retiring an application."
        placeholder={selected.length ? undefined : "All tables"}
        data={tables}
        value={selected}
        disabled={!editable || !tables.length}
        searchable
        clearable
        onChange={(v) => onChange("tables", v)}
      />
      <Select
        label="Archive into"
        description="Retired-system archives only — a document archive cannot hold tables."
        placeholder={containers.length ? "Select an archive" : "No dataset archives yet"}
        data={containers}
        value={containerId || null}
        disabled={!editable || !containers.length}
        searchable
        onChange={(v) => {
          onChange("containerId", v ?? "");
          onChange("containerName", containers.find((c) => c.value === v)?.label ?? "");
        }}
      />
      <TextInput
        label="Table name prefix"
        description="Optional — prepended to each archived table's name, e.g. pv_"
        value={String(config.namePrefix ?? "")}
        disabled={!editable}
        onChange={(e) => onChange("namePrefix", e.currentTarget.value)}
      />
      <Switch
        label="Include views"
        checked={Boolean(config.includeViews)}
        disabled={!editable}
        onChange={(e) => onChange("includeViews", e.currentTarget.checked)}
      />
    </Stack>
  );
}
